import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  CONNECTOR_ERROR_CODES,
  type ChatApprovalMode,
  type OrganizationAgentResponse,
} from '@laplace/contracts'
import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import type { ChatMessage, ChatSummary } from '@/features/chats/types/chat'
import {
  AssistantStreamClientError,
  streamAssistantMessage,
} from '@/api/chat-stream'
import { fetchWorkspaceConnectorStatus } from '@/features/chats/api/connector/fetch-workspace-connector-status'
import { useCreateWorkspaceChatMutation } from '@/features/chats/api/chats/mutations/create-chat-mutation'
import { useSendWorkspaceChatMessageMutation } from '@/features/chats/api/chats/mutations/send-message-mutation'
import {
  appendChatMessageToCache,
  flattenChatMessagePages,
  workspaceChatQueryKeys,
} from '@/features/chats/api/chats/queries/chats-query'
import { isChatGenerating } from '@/features/chats/domain/chat-generation-keys'
import {
  chatShellActions,
  chatShellStore,
  useChatShellStore,
} from '@/features/chats/domain/chat-shell.store'
import type { FailedGeneration } from '@/features/chats/domain/chat-shell.store'
import {
  getApiErrorCode,
  getPlanLimitNotification,
  isWorkspaceAccessErrorCode,
} from '@/shared/api/errors'
import { trackFirstMessageSent } from '@/shared/analytics'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { notificationActions } from '@/shared/notifications/notification-store'

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

/**
 * Moves a persisted assistant turn — a completed answer or the partial text
 * kept after a failed run — from the live streaming state into the caches.
 */
export function commitAssistantMessage(
  queryClient: QueryClient,
  workspaceId: string,
  chatId: string,
  message: ChatMessage,
) {
  const messageExists = flattenChatMessagePages(
    queryClient.getQueryData<InfiniteData<Array<ChatMessage>>>(
      workspaceChatQueryKeys.messages(workspaceId, chatId),
    ),
  ).some((cachedMessage) => cachedMessage.id === message.id)

  // Persist the trace under the final message id before clearing the live
  // chat-scoped state, so the collapsed panel survives below the answer for
  // the rest of the session.
  chatShellActions.commitTraceForMessage(chatId, message.id)
  chatShellActions.clearGenerationUiState(chatId)

  appendChatMessageToCache(queryClient, workspaceId, chatId, message)

  queryClient.setQueryData<Array<ChatSummary>>(
    workspaceChatQueryKeys.list(workspaceId),
    (current) => {
      if (!current) {
        return current
      }

      const existing = current.find((item) => item.id === chatId)
      if (!existing) {
        return current
      }

      const nextItem: ChatSummary = {
        ...existing,
        updatedAt: message.createdAt,
        lastMessageAt: message.createdAt,
        lastMessagePreview: message.content,
        messageCount: existing.messageCount + (messageExists ? 0 : 1),
      }

      return [nextItem, ...current.filter((item) => item.id !== chatId)]
    },
  )

  chatShellActions.setChatMessages(
    chatId,
    flattenChatMessagePages(
      queryClient.getQueryData<InfiniteData<Array<ChatMessage>>>(
        workspaceChatQueryKeys.messages(workspaceId, chatId),
      ),
    ),
  )
}

interface UseChatAssistantStreamParams {
  workspaceId: string
  currentChatId: string | null
  effectiveChatId: string | null
  agents: Array<OrganizationAgentResponse>
  selectedAgentId: string | null
  approvalMode: ChatApprovalMode
  hasWorkspaceAccess: boolean
  isCurrentChatGenerating: boolean
  streamingAssistantMessage: string | null
  inputValue: string
  draftContextId: string
  generationLockKey: string
  abortControllerKey: string | null
  failedGeneration: FailedGeneration | null
  createChatMutation: ReturnType<typeof useCreateWorkspaceChatMutation>
  sendMessageMutation: ReturnType<typeof useSendWorkspaceChatMessageMutation>
  setPendingMessage: (value: string | null) => void
  onChatErrorCode: (code: string | null) => void
  onStickToBottomChange: (stick: boolean) => void
}

export function useChatAssistantStream({
  workspaceId,
  currentChatId,
  effectiveChatId,
  agents,
  selectedAgentId,
  approvalMode,
  hasWorkspaceAccess,
  isCurrentChatGenerating,
  streamingAssistantMessage,
  inputValue,
  draftContextId,
  generationLockKey,
  abortControllerKey,
  failedGeneration,
  createChatMutation,
  sendMessageMutation,
  setPendingMessage,
  onChatErrorCode,
  onStickToBottomChange,
}: UseChatAssistantStreamParams) {
  const { t: tAgents } = useTranslation('agents')
  const { t } = useTranslation('workspace')
  const navigate = useNavigate()
  const lang = useCurrentLang()
  const queryClient = useQueryClient()
  const store = useChatShellStore()
  const handleRetryGenerationRef = useRef<() => Promise<void>>(async () => {})
  const connectorAutoResendLockRef = useRef(false)

  const sendMessage = async (chatId: string, content: string) => {
    return sendMessageMutation.mutateAsync({
      workspaceId,
      chatId,
      payload: { content },
    })
  }

  // Streams one assistant reply for an already-persisted user message. Shared by
  // the initial submit and the resend action. On a generation failure it records
  // the failed user message so the view can highlight it and offer a retry —
  // instead of just a toast that disappears. Caller owns the abort controller and
  // the per-chat generation-in-flight / orb locks.
  const runAssistantStream = async (
    resolvedChatId: string,
    content: string,
    streamAgentId: string | null,
    userMessageId: string | null,
    abortController: AbortController,
  ) => {
    const selectedStreamAgent = agents.find(
      (agent) => agent.id === streamAgentId,
    )
    chatShellActions.setGenerationAgent(
      resolvedChatId,
      selectedStreamAgent
        ? {
            id: selectedStreamAgent.id,
            handle: selectedStreamAgent.handle,
            name: selectedStreamAgent.name,
            iconKey: selectedStreamAgent.iconKey,
          }
        : null,
    )
    chatShellActions.setFailedGeneration(resolvedChatId, null)
    chatShellActions.setThinkingCollapsed(resolvedChatId, false)
    chatShellActions.setGenerationStatus(resolvedChatId, { phase: 'preparing' })
    chatShellActions.setAssistantTyping(resolvedChatId, true)

    try {
      await streamAssistantMessage(
        {
          workspaceId,
          chatId: resolvedChatId,
          content,
          ...(streamAgentId ? { agentId: streamAgentId } : {}),
        },
        {
          onStatus: (event) => {
            const status = {
              phase: event.phase,
              ...(event.tool ? { tool: event.tool } : {}),
            }
            chatShellActions.setGenerationStatus(resolvedChatId, status)
            chatShellActions.pushGenerationStep(resolvedChatId, status)
          },
          onDelta: (delta) => {
            chatShellActions.setAssistantTyping(resolvedChatId, false)
            const currentStreaming =
              chatShellStore.state.streamingAssistantMessageByChatId[
                resolvedChatId
              ] ?? ''
            // First token of the answer — fold the thinking trace so the reply
            // takes focus, but keep it available (collapsed) above the answer
            // instead of discarding the accumulated action timeline.
            if (currentStreaming === '') {
              chatShellActions.setThinkingCollapsed(resolvedChatId, true)
            }
            chatShellActions.setStreamingAssistantMessage(
              resolvedChatId,
              currentStreaming + delta,
            )
          },
          onArtifact: (artifact) => {
            chatShellActions.setAssistantTyping(resolvedChatId, false)
            chatShellActions.appendStreamingArtifact(resolvedChatId, artifact)
            const currentStreaming =
              chatShellStore.state.streamingAssistantMessageByChatId[
                resolvedChatId
              ] ?? ''
            if (currentStreaming === '') {
              chatShellActions.setThinkingCollapsed(resolvedChatId, true)
              // Keep the streaming bubble mounted even when artifacts arrive
              // before the first text delta.
              chatShellActions.setStreamingAssistantMessage(resolvedChatId, '')
            }
          },
          onDone: (assistantMessage) => {
            commitAssistantMessage(
              queryClient,
              workspaceId,
              resolvedChatId,
              assistantMessage,
            )
            chatShellActions.setChatMessagesError(resolvedChatId, null)
          },
          onApprovalRequired: (assistantMessage) => {
            commitAssistantMessage(
              queryClient,
              workspaceId,
              resolvedChatId,
              assistantMessage,
            )
            chatShellActions.setChatMessagesError(resolvedChatId, null)
          },
          onApprovalAutoApproved: (assistantMessage) => {
            commitAssistantMessage(
              queryClient,
              workspaceId,
              resolvedChatId,
              assistantMessage,
            )
            chatShellActions.setChatMessagesError(resolvedChatId, null)
          },
        },
        {
          signal: abortController.signal,
        },
      )
    } catch (error) {
      chatShellActions.clearGenerationUiState(resolvedChatId)
      if (isAbortError(error)) {
        return
      }

      const partialMessage =
        error instanceof AssistantStreamClientError
          ? error.partialMessage
          : undefined
      if (partialMessage) {
        commitAssistantMessage(
          queryClient,
          workspaceId,
          resolvedChatId,
          partialMessage,
        )
      }

      const limitNotification = await getPlanLimitNotification(error)
      if (limitNotification) {
        notificationActions.error(
          limitNotification.title,
          limitNotification.description,
        )
        return
      }

      const code = await getApiErrorCode(error)
      if (isWorkspaceAccessErrorCode(code)) {
        onChatErrorCode(code)
        return
      }

      const agentError = error instanceof Error ? error.message : ''
      if (agentError.includes('AGENT_SELECTOR_MENTION_CONFLICT')) {
        notificationActions.error(tAgents('agentSelectorMentionConflict'))
      } else if (agentError.includes('AGENT_NOT_FOUND')) {
        notificationActions.error(tAgents('agentUnavailable'))
      }

      // The user turn is already persisted; flag it so the view can mark the
      // message red and show a resend button instead of silently failing.
      const isConnectorOffline =
        error instanceof AssistantStreamClientError
          ? error.code === CONNECTOR_ERROR_CODES.CONNECTOR_OFFLINE
          : (error as Error & { code?: string })?.code ===
              CONNECTOR_ERROR_CODES.CONNECTOR_OFFLINE ||
            (error instanceof Error &&
              error.message.includes(CONNECTOR_ERROR_CODES.CONNECTOR_OFFLINE))

      if (isConnectorOffline) {
        // Allow one auto-resend for this new offline failure (previous lock may linger).
        connectorAutoResendLockRef.current = false
        notificationActions.error(
          t('connectorOfflineTitle'),
          t('connectorOfflineDescription'),
        )
      }

      chatShellActions.setFailedGeneration(resolvedChatId, {
        messageId: userMessageId,
        content,
        agentId: streamAgentId,
        ...(isConnectorOffline
          ? { reason: 'connector_offline' as const, autoResendAttempted: false }
          : {}),
      })
    }
  }

  const handleSubmit = async () => {
    const content = inputValue.trim()

    if (
      content.length === 0 ||
      isCurrentChatGenerating ||
      streamingAssistantMessage !== null ||
      !hasWorkspaceAccess
    ) {
      return
    }

    setPendingMessage(content)
    onStickToBottomChange(false)
    chatShellActions.clearDraft(draftContextId)
    let targetChatId: string | null = null
    const abortController = new AbortController()
    chatShellActions.setAbortController(generationLockKey, abortController)
    chatShellActions.setGenerationInFlight(generationLockKey, true)

    try {
      const isCreatingNewChat = currentChatId === null
      targetChatId =
        currentChatId ??
        (
          await createChatMutation.mutateAsync({
            workspaceId,
            payload: { approvalMode },
          })
        ).id

      const resolvedChatId = targetChatId

      if (generationLockKey !== resolvedChatId) {
        chatShellActions.setAbortController(generationLockKey, null)
        chatShellActions.setGenerationInFlight(generationLockKey, false)
      }

      chatShellActions.setAbortController(resolvedChatId, abortController)
      chatShellActions.setGenerationInFlight(resolvedChatId, true)
      chatShellActions.setAssistantTyping(resolvedChatId, true)
      chatShellActions.setGenerationStatus(resolvedChatId, {
        phase: 'preparing',
      })

      // Navigate as soon as the chat exists so the route owns the shell (and
      // New chat can detach cleanly). Stream continues under resolvedChatId.
      if (isCreatingNewChat) {
        trackFirstMessageSent({ workspace_id: workspaceId })
        chatShellActions.moveSelectedAgent(
          draftContextId,
          `chat:${resolvedChatId}`,
        )
        chatShellActions.setCurrentChatId(resolvedChatId)
        navigate({
          to: '/$lang/workspace/$workspaceId/chats/$chatId',
          params: { lang, workspaceId, chatId: resolvedChatId },
          replace: true,
        })
      }

      const sentMessage = await sendMessage(resolvedChatId, content)
      setPendingMessage(null)

      await runAssistantStream(
        resolvedChatId,
        content,
        selectedAgentId ??
          agents.find(
            (agent) =>
              agent.handle.toLocaleLowerCase() ===
              content
                .match(
                  /^@([\p{L}\p{N}][\p{L}\p{N}-]{1,63})(?=\s|[.,!?;:]|$)/u,
                )?.[1]
                ?.toLocaleLowerCase(),
          )?.id ??
          null,
        sentMessage.id,
        abortController,
      )
    } catch (error) {
      if (targetChatId) {
        chatShellActions.clearGenerationUiState(targetChatId)
      }
      chatShellActions.clearGenerationUiState(generationLockKey)
      if (isAbortError(error)) {
        return
      }

      const limitNotification = await getPlanLimitNotification(error)
      if (limitNotification) {
        notificationActions.error(
          limitNotification.title,
          limitNotification.description,
        )
        return
      }

      const code = await getApiErrorCode(error)
      if (currentChatId && isWorkspaceAccessErrorCode(code)) {
        onChatErrorCode(code)
      } else {
        notificationActions.error(
          'Reply generation failed',
          'The message was saved, but Laplace did not return a reply. Please try again.',
        )
      }
    } finally {
      chatShellActions.setAbortController(generationLockKey, null)
      if (targetChatId) {
        chatShellActions.setAbortController(targetChatId, null)
        chatShellActions.setGenerationInFlight(targetChatId, false)
        chatShellActions.setAssistantTyping(targetChatId, false)
      }
      setPendingMessage(null)
      chatShellActions.setGenerationInFlight(generationLockKey, false)
    }
  }

  const handleAbortSubmit = () => {
    if (abortControllerKey === null) {
      return
    }
    store.abortControllerByChatId[abortControllerKey]?.abort()
  }

  const handleRetryGeneration = async () => {
    const chatId = effectiveChatId
    if (chatId === null) {
      return
    }

    const failed = store.failedGenerationByChatId[chatId]
    if (!failed || isChatGenerating(store, chatId) || !hasWorkspaceAccess) {
      return
    }

    const abortController = new AbortController()
    chatShellActions.setAbortController(chatId, abortController)
    chatShellActions.setGenerationInFlight(chatId, true)

    try {
      await runAssistantStream(
        chatId,
        failed.content,
        failed.agentId ?? null,
        failed.messageId,
        abortController,
      )
    } finally {
      chatShellActions.setAbortController(chatId, null)
      chatShellActions.setGenerationInFlight(chatId, false)
      chatShellActions.setAssistantTyping(chatId, false)
    }
  }

  handleRetryGenerationRef.current = handleRetryGeneration

  // LP-113: poll connector status after an offline failure; toast + one-shot auto-resend.
  useEffect(() => {
    if (
      !workspaceId ||
      effectiveChatId === null ||
      !failedGeneration ||
      failedGeneration.reason !== 'connector_offline' ||
      failedGeneration.autoResendAttempted
    ) {
      return
    }

    let cancelled = false

    const poll = async () => {
      try {
        if (connectorAutoResendLockRef.current) {
          return
        }
        const status = await fetchWorkspaceConnectorStatus(workspaceId)
        if (cancelled || !status.online) {
          return
        }

        connectorAutoResendLockRef.current = true
        notificationActions.success(
          t('connectorBackOnlineTitle'),
          t('connectorBackOnlineDescription'),
        )
        chatShellActions.setFailedGeneration(effectiveChatId, {
          ...failedGeneration,
          autoResendAttempted: true,
        })
        await handleRetryGenerationRef.current()
      } catch {
        // Ignore transient status poll errors.
      }
    }

    void poll()
    const intervalId = window.setInterval(() => {
      void poll()
    }, 5_000)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
  }, [workspaceId, effectiveChatId, failedGeneration, t])

  return {
    handleSubmit,
    handleAbortSubmit,
    handleRetryGeneration,
  }
}
